import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import Container from '../../Components/Container'
import DataTable from 'react-data-table-component'
import toast, { Toaster } from 'react-hot-toast'
import { FiPlus, FiEdit2, FiTrash2, FiBell, FiSearch, FiX } from 'react-icons/fi'
import { motion, AnimatePresence } from 'framer-motion'

const readJson = async (res) => {
    if (!res.ok) throw new Error('Sunucu yanıt vermedi')
    return res.json()
}

const isListError = (payload) =>
    payload != null &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    payload.status === 400

const isSuccess = (payload) =>
    payload != null && typeof payload === 'object' && Number(payload.status) === 200

const splitPickerValue = (raw) => {
    const v = String(raw ?? '').trim()
    if (!v) return { name: '', barcode: '' }
    const i = v.lastIndexOf('-')
    if (i <= 0) return { name: v, barcode: '' }
    return { name: v.slice(0, i).trim(), barcode: v.slice(i + 1).trim() }
}

const Notification = () => {
    const [open, setOpen] = useState(false)
    const [openBulk, setOpenBulk] = useState(false)
    const [product, setProduct] = useState('')
    const [pickerValue, setPickerValue] = useState('')
    const [editingId, setEditingId] = useState(null)
    const [count, setCount] = useState(1)
    const [bulkCount, setBulkCount] = useState(1)
    const [isEditMode, setIsEditMode] = useState(false)
    const [rows, setRows] = useState([])
    const [products, setProducts] = useState([])
    const [barcode, setBarcode] = useState('')
    const [filterText, setFilterText] = useState('')
    const [listLoading, setListLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [bulkSubmitting, setBulkSubmitting] = useState(false)

    const productInputRef = useRef(null)

    const loadNotifications = useCallback(async () => {
        const formdata = new FormData()
        formdata.append('action', 'select')
        const payload = await fetch(
            process.env.REACT_APP_BASE_URL + 'notification.php',
            { method: 'POST', body: formdata }
        ).then(readJson)

        if (isListError(payload)) {
            toast.error(payload.message || 'Bildirimler alınamadı')
            setRows([])
            return
        }
        setRows(Array.isArray(payload) ? payload : [])
    }, [])

    const loadProducts = useCallback(async () => {
        const formdata = new FormData()
        formdata.append('action', 'select')
        const payload = await fetch(
            process.env.REACT_APP_BASE_URL + 'product.php',
            { method: 'POST', body: formdata }
        ).then(readJson)

        if (isListError(payload)) {
            toast.error(payload.message || 'Ürünler alınamadı')
            setProducts([])
            return
        }
        setProducts(Array.isArray(payload) ? payload : [])
    }, [])

    useEffect(() => {
        let cancelled = false
        setListLoading(true)
        ;(async () => {
            try {
                await Promise.all([loadNotifications(), loadProducts()])
            } catch {
                if (!cancelled) toast.error('Veriler yüklenirken hata oluştu')
            } finally {
                if (!cancelled) setListLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [loadNotifications, loadProducts])

    const resetEditModal = useCallback(() => {
        setOpen(false)
        setIsEditMode(false)
        setEditingId(null)
        setProduct('')
        setBarcode('')
        setPickerValue('')
        setCount(1)
    }, [])

    const openCreateModal = () => {
        setIsEditMode(false)
        setEditingId(null)
        setProduct('')
        setBarcode('')
        setPickerValue('')
        setCount(1)
        setOpen(true)
    }

    const openEditModal = (row) => {
        setIsEditMode(true)
        setEditingId(row.id)
        const prod = row.product ?? ''
        const bc = row.barcode ?? ''
        setProduct(prod)
        setBarcode(bc)
        setPickerValue([prod, bc].filter(Boolean).join('-'))
        setCount(row.count ?? 1)
        setOpen(true)
    }

    const closeEditModal = () => {
        if (submitting) return
        resetEditModal()
    }

    const openBulkModal = () => {
        setBulkCount(1)
        setOpenBulk(true)
    }

    const closeBulkModal = () => {
        if (bulkSubmitting) return
        setOpenBulk(false)
    }

    useEffect(() => {
        const anyOpen = open || openBulk
        if (!anyOpen) return
        const ref = open ? productInputRef : null
        const raf = requestAnimationFrame(() => ref?.current?.focus())
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (open && !submitting) closeEditModal()
                if (openBulk && !bulkSubmitting) closeBulkModal()
            }
        }
        document.addEventListener('keydown', onKey)
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prev
        }
    }, [open, openBulk, submitting, bulkSubmitting])

    const optionHandel = (e) => {
        const val = e.target.value
        setPickerValue(val)
        const { name, barcode: bc } = splitPickerValue(val)
        setProduct(name)
        setBarcode(bc)
    }

    const submitInsert = async () => {
        const p = String(product).trim()
        const bc = String(barcode).trim()
        const n = Number(count)
        if (!p || !bc) {
            toast.error('Ürün listeden seçin (ad ve barkod gerekli)')
            return
        }
        if (!Number.isFinite(n) || n < 0) {
            toast.error('Geçerli bir adet girin')
            return
        }

        setSubmitting(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'insert')
            formdata.append('product', p)
            formdata.append('count', String(n))
            formdata.append('barcode', bc)
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'notification.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isSuccess(data)) {
                toast.error(data.message || 'Kayıt başarısız')
                return
            }
            toast.success('Bildirim kaydedildi')
            resetEditModal()
            await loadNotifications()
        } catch {
            toast.error('İstek başarısız oldu')
        } finally {
            setSubmitting(false)
        }
    }

    const submitUpdate = async () => {
        const p = String(product).trim()
        const bc = String(barcode).trim()
        const n = Number(count)
        if (editingId == null) {
            toast.error('Kayıt bulunamadı')
            return
        }
        if (!p || !bc) {
            toast.error('Ürün ve barkod gerekli')
            return
        }
        if (!Number.isFinite(n) || n < 0) {
            toast.error('Geçerli bir adet girin')
            return
        }

        setSubmitting(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'update')
            formdata.append('id', String(editingId))
            formdata.append('product', p)
            formdata.append('barcode', bc)
            formdata.append('count', String(n))
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'notification.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isSuccess(data)) {
                toast.error(data.message || 'Güncelleme başarısız')
                return
            }
            toast.success('Bildirim güncellendi')
            resetEditModal()
            await loadNotifications()
        } catch {
            toast.error('İstek başarısız oldu')
        } finally {
            setSubmitting(false)
        }
    }

    const allItemsSet = async () => {
        const n = Number(bulkCount)
        if (!Number.isFinite(n) || n < 0) {
            toast.error('Geçerli bir minimum stok adedi girin')
            return
        }

        setBulkSubmitting(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'allİtemsSet')
            formdata.append('count', String(n))
            formdata.append('role', localStorage.getItem('role') ?? '')
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'notification.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isSuccess(data)) {
                toast.error(data.message || 'Toplu işlem başarısız')
                return
            }
            toast.success(data.message || 'Toplu ayar uygulandı')
            setOpenBulk(false)
            await loadNotifications()
        } catch {
            toast.error('İstek başarısız oldu')
        } finally {
            setBulkSubmitting(false)
        }
    }

    const deleteRow = async (rowId) => {
        try {
            const formdata = new FormData()
            formdata.append('action', 'delete')
            formdata.append('id', String(rowId))
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'notification.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isSuccess(data)) {
                toast.error(data.message || 'Silinemedi')
                return
            }
            toast.success('Bildirim silindi')
            await loadNotifications()
        } catch {
            toast.error('İstek başarısız oldu')
        }
    }

    const filteredRows = useMemo(() => {
        const q = filterText.trim().toLowerCase()
        if (!q) return rows
        return rows.filter((row) =>
            String(row.product ?? '')
                .toLowerCase()
                .includes(q)
        )
    }, [rows, filterText])

    const columns = useMemo(
        () => [
            {
                name: 'No',
                selector: (r) => r.id,
                sortable: true,
                width: '80px',
            },
            {
                name: 'Ürün',
                selector: (r) =>
                    String(r.product ?? '').replace(/_/g, ' '),
                sortable: true,
                grow: 2,
            },
            {
                name: 'Adet',
                selector: (r) => r.count,
                sortable: true,
                center: true,
                width: '120px',
            },
            {
                name: 'İşlem',
                cell: (r) => (
                    <div className='flex items-center gap-2'>
                        <button
                            type='button'
                            onClick={() => openEditModal(r)}
                            className='rounded-xl p-2 text-indigo-600 transition hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                            title='Düzenle'
                        >
                            <FiEdit2 size={18} />
                        </button>
                        <button
                            type='button'
                            onClick={() => deleteRow(r.id)}
                            className='rounded-xl p-2 text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2'
                            title='Sil'
                        >
                            <FiTrash2 size={18} />
                        </button>
                    </div>
                ),
                width: '120px',
            },
        ],
        []
    )

    const customStyles = useMemo(
        () => ({
            table: {
                style: {
                    backgroundColor: 'white',
                    borderRadius: '0.75rem',
                },
            },
            headRow: {
                style: {
                    backgroundColor: '#f8fafc',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e2e8f0',
                },
            },
            headCells: {
                style: {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '1rem',
                },
            },
            cells: {
                style: {
                    fontSize: '0.875rem',
                    padding: '1rem',
                    color: '#334155',
                },
            },
        }),
        []
    )

    const inputClass =
        'w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60'

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{ className: 'text-sm font-medium shadow-lg' }}
            />

            <AnimatePresence>
                {open && (
                    <motion.div
                        key='notif-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !submitting && closeEditModal()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='w-full max-w-md overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <h2 className='text-lg font-bold text-slate-900'>
                                        {isEditMode
                                            ? 'Bildirimi düzenle'
                                            : 'Yeni bildirim'}
                                    </h2>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            !submitting && closeEditModal()
                                        }
                                        disabled={submitting}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>
                            </div>
                            <div className='space-y-4 px-6 py-6'>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Ürün
                                    </label>
                                    <input
                                        ref={productInputRef}
                                        list='notification-products'
                                        value={pickerValue}
                                        onChange={optionHandel}
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Listeden seçin veya yazın…'
                                        autoComplete='off'
                                    />
                                    <datalist id='notification-products'>
                                        {products.map((pr) => (
                                            <option
                                                key={
                                                    pr.id ??
                                                    pr.barcode ??
                                                    `${pr.name}-${pr.barcode}`
                                                }
                                                value={`${pr.name ?? ''}-${pr.barcode ?? ''}`}
                                            />
                                        ))}
                                    </datalist>
                                </div>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Minimum adet
                                    </label>
                                    <input
                                        type='number'
                                        min={0}
                                        value={count}
                                        onChange={(e) =>
                                            setCount(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Minimum stok adedi'
                                    />
                                </div>
                            </div>
                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={closeEditModal}
                                    disabled={submitting}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={
                                        isEditMode ? submitUpdate : submitInsert
                                    }
                                    disabled={submitting}
                                    className='inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-60'
                                >
                                    {submitting ? (
                                        <span className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : isEditMode ? (
                                        'Güncelle'
                                    ) : (
                                        'Kaydet'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {openBulk && (
                    <motion.div
                        key='bulk-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !bulkSubmitting && closeBulkModal()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='w-full max-w-md overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <h2 className='text-lg font-bold text-slate-900'>
                                        Toplu bildirim ayarı
                                    </h2>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            !bulkSubmitting && closeBulkModal()
                                        }
                                        disabled={bulkSubmitting}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>
                            </div>
                            <div className='px-6 py-6'>
                                <div className='mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4'>
                                    <FiBell className='mt-0.5 shrink-0 text-amber-500' />
                                    <div>
                                        <p className='text-sm font-semibold text-amber-900'>
                                            Önemli
                                        </p>
                                        <p className='mt-1 text-sm leading-relaxed text-amber-800'>
                                            Bu işlem tüm ürünler için geçerlidir;
                                            mevcut bildirim ayarları sıfırlanır.
                                        </p>
                                    </div>
                                </div>
                                <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                    Minimum stok adedi
                                </label>
                                <input
                                    type='number'
                                    min={0}
                                    value={bulkCount}
                                    onChange={(e) =>
                                        setBulkCount(e.target.value)
                                    }
                                    disabled={bulkSubmitting}
                                    className={inputClass}
                                    placeholder='Tüm ürünler için'
                                />
                            </div>
                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={closeBulkModal}
                                    disabled={bulkSubmitting}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={allItemsSet}
                                    disabled={bulkSubmitting}
                                    className='inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700 disabled:pointer-events-none disabled:opacity-60'
                                >
                                    {bulkSubmitting ? (
                                        <span className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : (
                                        'Uygula'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto w-full max-w-[1400px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8'>
                    <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex items-start gap-4'>
                            <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25'>
                                <FiBell className='text-xl' />
                            </div>
                            <div>
                                <h1 className='text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl'>
                                    Stok bildirimleri
                                </h1>
                                <p className='mt-1 max-w-xl text-sm text-slate-600'>
                                    Minimum stok eşiğine göre uyarıları
                                    yönetin.
                                </p>
                                <p className='mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                                    {filteredRows.length} kayıt
                                    {filterText.trim() ? ' (filtreli)' : ''}
                                </p>
                            </div>
                        </div>
                        <div className='flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center'>
                            <div className='relative min-w-0 sm:min-w-[16rem]'>
                                <FiSearch className='pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                                <input
                                    type='text'
                                    value={filterText}
                                    onChange={(e) =>
                                        setFilterText(e.target.value)
                                    }
                                    placeholder='Ürün ara…'
                                    className={`${inputClass} pl-10`}
                                />
                            </div>
                            <div className='flex shrink-0 flex-wrap items-center gap-2'>
                                <button
                                    type='button'
                                    onClick={openCreateModal}
                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700'
                                >
                                    <FiPlus size={18} />
                                    Yeni bildirim
                                </button>
                                <button
                                    type='button'
                                    onClick={openBulkModal}
                                    className='inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700'
                                >
                                    Toplu ayarla
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm'>
                    <DataTable
                        columns={columns}
                        data={filteredRows}
                        pagination
                        progressPending={listLoading}
                        progressComponent={
                            <div className='flex flex-col items-center justify-center gap-3 py-20'>
                                <div className='h-10 w-10 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent' />
                                <p className='text-sm font-medium text-slate-600'>
                                    Yükleniyor…
                                </p>
                            </div>
                        }
                        customStyles={customStyles}
                        noDataComponent={
                            <div className='py-12 text-center text-sm text-slate-500'>
                                {filterText.trim()
                                    ? 'Aramaya uygun kayıt yok.'
                                    : 'Henüz bildirim yok.'}
                            </div>
                        }
                    />
                </div>
            </div>
        </Container>
    )
}

export default Notification
